import EventEmitter from "eventemitter3";
import { config } from "~/config";

import { WalletTgSdk } from "@uxuycom/web3-tg-sdk";
import EVMConnectionWrapper from "./EVMConnectionWrapper";
import SolanaConnectionWrapper from "./SolanaConnectionWrapper";
import ConnectionWrapper, { CHAIN_IDS } from "./ConnectionWrapper";
import { getProviderAlliance } from "./ChainKeyMaps";
import { timout } from "~/utils/common";

class WalletConnectionStore extends EventEmitter {
    #tgSdk: any;

    providers: Array<ConnectionWrapper>;
    supportedChains = [CHAIN_IDS.ETH, CHAIN_IDS.SOLANA].map((item) =>
        String(item)
    );

    constructor() {
        super();

        this.#tgSdk = this.#initializeTGSDK();

        //@ts-ignore
        const ethereumProvider = new EVMConnectionWrapper(this.#tgSdk.ethereum);
        //@ts-ignore
        const solanaProvider = new SolanaConnectionWrapper(this.#tgSdk.solana);

        this.providers = [ethereumProvider, solanaProvider];
        this.providers.forEach((provider) => {
            this.#subscribeProvider(provider);
        });
    }

    getSnapshot = () => {
        return this.providers.map((p) => p.address).join(" ");
    };

    subscribe = (listener: EventListener) => {
        this.on("subscribe", listener);
        return () => {
            this.off("subscribe", listener);
        };
    };

    unsafe_reset() {
        this.#tgSdk = this.#initializeTGSDK();

        //@ts-ignore
        const ethereumProvider = new EVMConnectionWrapper(this.#tgSdk.ethereum);
        //@ts-ignore
        const solanaProvider = new SolanaConnectionWrapper(this.#tgSdk.solana);

        this.providers = [ethereumProvider, solanaProvider];
        this.providers.forEach((provider) => {
            this.#subscribeProvider(provider);
        });

        this.emit("subscribe");
    }

    #initializeTGSDK() {
        const cachedLink = window.localStorage.getItem(
            "__uxuy_cache_wallet_direct_link__"
        );

        const tgSdk = new WalletTgSdk({
            connect_direct_link: cachedLink ?? config.wallet_direct_link,
            metaData: {
                hostname: window.location.hostname,
                icon: "https://static.uxuy.me/images/dex/dex_icon.png",
            },
        });

        //@ts-ignore
        if (!tgSdk.ethereum || !tgSdk.solana) {
            throw new Error("Initialize @uxuy/web3-tg-sdk failed!");
        }

        return tgSdk;
    }

    #subscribeProvider(wrapper: ConnectionWrapper) {
        const provider = wrapper.provider;
        provider.removeAllListeners();
        provider.on("accountsChanged", ([address]) => {
            wrapper.address = address;
            this.emit("subscribe");
        });
        provider.on("disconnect", async () => {
            wrapper.address = null;
            this.emit("subscribe");
        });
    }

    isConnected() {
        return this.providers.some((provider) => provider.isConnected());
    }

    findProviderBy(chainId: string) {
        const alliance = getProviderAlliance(chainId);
        return this.providers.find((p) => p.alliance === alliance);
    }

    isConnectedTo(chainId: string) {
        const provider = this.findProviderBy(chainId);
        return Boolean(provider && provider.isConnected());
    }

    async connect() {
        const chainId = this.supportedChains[0];
        return this.connectTo(chainId);
    }

    async connectTo(chainId: string) {
        if (this.isConnectedTo(chainId)) return;
        const provider = this.findProviderBy(chainId);

        if (!provider) return;
        try {
            await Promise.race([provider.connect(), timout(60 * 1000)]);
            for (const p of this.providers) {
                await p?.getAddressSilently();
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.emit("subscribe");
        }
    }

    async disconnect() {
        try {
            for (const provider of this.providers) {
                await provider.disconnect();
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.emit("subscribe");
        }
    }

    getConnectedAddressByChainId(chainId: string) {
        const provider = this.findProviderBy(chainId);

        if (!provider) return;

        return provider.address;
    }
}

const connectionStore = new WalletConnectionStore();

export default connectionStore;
